import React, { useState, useEffect } from 'react'
import { Button } from '@disco/accounts/components/ui/button'
import { Input } from '@disco/accounts/components/ui/input'
import { Label } from '@disco/accounts/components/ui/label'
import DomainUrlField from './DomainUrlField'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@disco/accounts/components/ui/select'
import { Checkbox } from '@disco/accounts/components/ui/checkbox'
import { cn } from '@disco/accounts/utils/utils'
import { SignupInput, SignupStep, useBusinessRoleOptions } from './signup-data'
import { createStepValidationSchema } from './validation-schemas'
import * as yup from 'yup'
import { useAppState } from '@disco/accounts/hooks/use-app-state'
import { useTranslation } from '@disco/i18n/use-translation'
import { Trans } from 'react-i18next'

interface DynamicFormProps {
  step: SignupStep
  onNext: (data: Record<string, unknown>) => void
  onBack?: () => void
  data: Record<string, unknown>
  isLoading: boolean
  stepIndex: number
  isLastStep: boolean
}

export default function DynamicForm({
  step,
  onNext,
  onBack,
  data,
  isLoading,
  stepIndex,
  isLastStep,
}: DynamicFormProps) {
  const { t } = useTranslation('accounts')
  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const appState = useAppState()
  const BUSINESS_ROLE_OPTIONS = useBusinessRoleOptions()

  const submitIsDisabled =
    isLoading ||
    appState.database_readonly_mode ||
    !appState.modules_state.signups

  useEffect(() => {
    // Initialize checkbox values to false if not present
    const initializedData = { ...data }
    step.inputs.forEach(input => {
      if (
        input.type === 'checkbox' &&
        initializedData[input.name] === undefined
      ) {
        initializedData[input.name] = false
      }
    })
    setFormData(initializedData)
  }, [data, step.inputs])

  const handleInputChange = (name: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [name]: value }))

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
    // Reset dependent role when businessType changes
    if (name === 'businessType') {
      setFormData(prev => ({ ...prev, user_role: '' }))
    }

    if (name === 'business_name') {
      const projectName = value as string
      const subdomain = projectName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .substring(0, 50)
      setFormData(prev => ({ ...prev, subdomain }))
    }

    if (
      name === 'verification_source' ||
      name === 'verification_source_1' ||
      name === 'verification_source_2'
    ) {
      const source = value as string
      const handlePlaceholders: Record<string, string> = {
        website_url: t(
          'accounts:enterYourWebsiteUrl',
          'Enter your Website URL',
        ),
        instagram_handle: t(
          'accounts:enterYourInstagramUsername',
          'Enter your Instagram username',
        ),
        twitter_handle: t(
          'accounts:enterYourTwitterHandle',
          'Enter your Twitter handle',
        ),
        facebook_url: t(
          'accounts:enterYourFacebookUrl',
          'Enter your Facebook URL',
        ),
        youtube_url: t(
          'accounts:enterYourYoutubeUrl',
          'Enter your YouTube URL',
        ),
        soundcloud_url: t(
          'accounts:enterYourSoundcloudUrl',
          'Enter your SoundCloud URL',
        ),
        spotify_url: t(
          'accounts:enterYourSpotifyUrl',
          'Enter your Spotify URL',
        ),
        apple_music_url: t(
          'accounts:enterYourAppleMusicUrl',
          'Enter your Apple Music URL',
        ),
        bandcamp_url: t(
          'accounts:enterYourBandcampUrl',
          'Enter your Bandcamp URL',
        ),
        tiktok_handle: t(
          'accounts:enterYourTiktokUsername',
          'Enter your TikTok username',
        ),
        tidal_music_url: t(
          'accounts:enterYourTidalUrl',
          'Enter your Tidal URL',
        ),
        ipi_number: t('accounts:enterYourIpiNumber', 'Enter your IPI number'),
        other_description: '',
      }

      const handleFieldName =
        name === 'verification_source'
          ? 'verification_handle'
          : name === 'verification_source_1'
          ? 'verification_handle_1'
          : 'verification_handle_2'

      let handleField = step.inputs.find(
        input => input.name === handleFieldName,
      )

      switch (name) {
        case 'verification_source': {
          handleField = step.inputs
            .find(input => input.name === 'verification_1')
            ?.fields?.find(field => field.name === 'verification_handle')
          break
        }
        case 'verification_source_1': {
          handleField = step.inputs
            .find(input => input.name === 'verification_1')
            ?.fields?.find(field => field.name === 'verification_handle_1')
          break
        }
        case 'verification_source_2': {
          handleField = step.inputs
            .find(input => input.name === 'verification_2')
            ?.fields?.find(field => field.name === 'verification_handle_2')
          break
        }
      }
      if (handleField) {
        handleField.placeholder =
          handlePlaceholders[source] ??
          t('accounts:enterYourUsername', 'Enter your username')
      }
    }
  }

  const validateForm = async () => {
    try {
      const schema = createStepValidationSchema(step.inputs)
      await schema.validate(formData, { abortEarly: false })
      setErrors({})
      return true
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const newErrors: Record<string, string> = {}
        error.inner.forEach(err => {
          if (err.path) {
            newErrors[err.path] = err.message
          }
        })
        setErrors(newErrors)
      }
      return false
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const isValid = await validateForm()
    if (isValid) {
      onNext(formData)
    }
  }

  const renderInput = (input: SignupInput) => {
    const value = formData[input.name] || ''
    const hasError = !!errors[input.name]

    switch (input.type) {
      case 'text':
        if (input.name === 'subdomain') {
          return (
            <div key={input.name} className="space-y-2">
              <Label
                htmlFor={input.name}
                className="text-black font-circular text-sm font-medium leading-4"
              >
                {input.label} <span>{input.required && '*'}</span>
              </Label>
              <DomainUrlField
                value={value as string}
                onChange={v => handleInputChange(input.name, v)}
                placeholder={input.placeholder}
                error={hasError ? errors[input.name] : undefined}
              />
            </div>
          )
        }
        return (
          <div key={input.name} className="space-y-2">
            <Label
              htmlFor={input.name}
              className="text-black font-circular text-sm font-medium leading-4"
            >
              {input.label} <span>{input.required && '*'}</span>
            </Label>
            <Input
              id={input.name}
              type="text"
              placeholder={input.placeholder}
              value={value as string}
              onChange={e => handleInputChange(input.name, e.target.value)}
              className={cn(
                'w-full h-11 border border-gray-200 rounded-sm bg-gray-50 font-circular text-base font-normal leading-5 tracking-normal px-3',
                hasError && 'border-red-500 focus:border-red-500',
              )}
              disabled={isLoading}
            />
            {hasError && (
              <p className="text-sm text-red-600">{errors[input.name]}</p>
            )}
          </div>
        )

      case 'select':
        // Hide role selector until a business type is selected
        if (
          input.name === 'user_role' &&
          (formData['businessType'] === undefined ||
            formData['businessType'] === '') &&
          // Only hide for the business flow; artist flow provides static options inline
          !input.options?.length
        ) {
          return null
        }
        return (
          <div key={input.name} className="space-y-2">
            <Label
              htmlFor={input.name}
              className="text-black font-circular text-sm font-medium leading-4"
            >
              {input.label} <span>{input.required && '*'}</span>
            </Label>
            <Select
              value={value as string}
              onValueChange={newValue =>
                handleInputChange(input.name, newValue)
              }
              disabled={isLoading}
            >
              <SelectTrigger
                className={cn(
                  'w-full h-11 border border-gray-200 rounded-sm bg-gray-50 font-circular text-base font-normal leading-5 tracking-normal',
                  hasError && 'border-red-500 focus:border-red-500',
                )}
              >
                <SelectValue placeholder={input.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {(input.name === 'user_role'
                  ? // Prefer static options when present (artist flow); otherwise use mapping by business type
                    input.options && input.options.length > 0
                    ? input.options
                    : BUSINESS_ROLE_OPTIONS[
                        (formData['businessType'] as string) || ''
                      ] || []
                  : input.options || []
                ).map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasError && (
              <p className="text-sm text-red-600">{errors[input.name]}</p>
            )}
          </div>
        )

      case 'radio':
        return (
          <div key={input.name} className="space-y-2">
            <Label className="text-black font-circular text-sm font-medium leading-4">
              {input.label} <span>{input.required && '*'}</span>
            </Label>
            <div className="space-y-3">
              {input.options?.map(option => (
                <div key={option.value} className="flex items-start space-x-3">
                  <input
                    type="radio"
                    id={`${input.name}-${option.value}`}
                    name={input.name}
                    value={option.value}
                    checked={value === option.value}
                    onChange={e =>
                      handleInputChange(input.name, e.target.value)
                    }
                    className="mt-1"
                    disabled={isLoading}
                  />
                  <div className="flex-1">
                    <label
                      htmlFor={`${input.name}-${option.value}`}
                      className="text-base font-medium cursor-pointer"
                    >
                      {option.label}
                    </label>
                    {option.description && (
                      <p className="text-sm text-gray-600 mt-1">
                        {option.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {hasError && (
              <p className="text-sm text-red-600">{errors[input.name]}</p>
            )}
          </div>
        )

      case 'type_selection':
        return (
          <div key={input.name} className="space-y-2">
            <Label className="text-black font-circular text-sm font-medium leading-4">
              {input.label} <span>{input.required && '*'}</span>
            </Label>
            <div className="space-y-3">
              {input.options?.map(option => (
                <div
                  key={option.value}
                  className={cn(
                    'relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:border-gray-300',
                    value === option.value
                      ? 'border-[#5865f2] bg-[#5865f2]/5'
                      : 'border-gray-200 bg-white',
                  )}
                  onClick={() => handleInputChange(input.name, option.value)}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      {option.icon === 'artist' ? (
                        <svg
                          className="w-5 h-5"
                          fill="#344563"
                          viewBox="0 0 16 16"
                        >
                          <path d="M9.8516 10.3792C8.20202 10.3792 6.86052 9.03621 6.86052 7.38479C6.86052 5.73338 8.20202 4.39038 9.8516 4.39038C11.5012 4.39038 12.8427 5.73338 12.8427 7.38479C12.8427 9.03621 11.5012 10.3792 9.8516 10.3792ZM9.8516 5.13898C8.61479 5.13898 7.60829 6.1466 7.60829 7.38479C7.60829 8.62298 8.61479 9.6306 9.8516 9.6306C11.0884 9.6306 12.0949 8.62298 12.0949 7.38479C12.0949 6.1466 11.0884 5.13898 9.8516 5.13898ZM13.9643 14.4965V13.7479C13.9643 12.3031 12.7903 11.1278 11.3471 11.1278H8.35606C6.91286 11.1278 5.73886 12.3031 5.73886 13.7479V14.4965C5.73886 14.5958 5.77825 14.691 5.84837 14.7612C5.91848 14.8314 6.01358 14.8708 6.11274 14.8708C6.2119 14.8708 6.307 14.8314 6.37712 14.7612C6.44724 14.691 6.48663 14.5958 6.48663 14.4965V13.7479C6.48663 12.7156 7.32488 11.8764 8.35606 11.8764H11.3471C12.3783 11.8764 13.2166 12.7156 13.2166 13.7479V14.4965C13.2166 14.5958 13.256 14.691 13.3261 14.7612C13.3962 14.8314 13.4913 14.8708 13.5905 14.8708C13.6896 14.8708 13.7847 14.8314 13.8548 14.7612C13.925 14.691 13.9643 14.5958 13.9643 14.4965ZM6.48663 10.0049C6.48663 9.90563 6.44724 9.81043 6.37712 9.74023C6.307 9.67004 6.2119 9.6306 6.11274 9.6306C4.87593 9.6306 3.86943 8.62298 3.86943 7.38479C3.86943 6.1466 4.87593 5.13898 6.11274 5.13898C6.2119 5.13898 6.307 5.09955 6.37712 5.02935C6.44724 4.95916 6.48663 4.86395 6.48663 4.76468C6.48663 4.66541 6.44724 4.57021 6.37712 4.50001C6.307 4.42982 6.2119 4.39038 6.11274 4.39038C4.46316 4.39038 3.12166 5.73338 3.12166 7.38479C3.12166 9.03621 4.46316 10.3792 6.11274 10.3792C6.2119 10.3792 6.307 10.3398 6.37712 10.2696C6.44724 10.1994 6.48663 10.1042 6.48663 10.0049ZM2.74777 14.4965V13.7479C2.74777 12.7156 3.58602 11.8764 4.6172 11.8764H5.36497C5.46413 11.8764 5.55923 11.837 5.62935 11.7668C5.69947 11.6966 5.73886 11.6014 5.73886 11.5021C5.73886 11.4028 5.69947 11.3076 5.62935 11.2374C5.55923 11.1672 5.46413 11.1278 5.36497 11.1278H4.6172C3.174 11.1278 2 12.3031 2 13.7479V14.4965C2 14.5958 2.03939 14.691 2.10951 14.7612C2.17963 14.8314 2.27473 14.8708 2.37389 14.8708C2.47305 14.8708 2.56815 14.8314 2.63826 14.7612C2.70838 14.691 2.74777 14.5958 2.74777 14.4965Z" />
                        </svg>
                      ) : option.icon === 'business' ? (
                        <svg
                          className="w-5 h-5"
                          fill="#344563"
                          viewBox="0 0 16 16"
                        >
                          <path d="M6 6.26538V5.39999C6 5.11233 6.10278 4.87215 6.30833 4.67944C6.51389 4.48674 6.77008 4.39038 7.07692 4.39038H8.92308C9.22992 4.39038 9.48611 4.48674 9.69167 4.67944C9.89722 4.87215 10 5.11233 10 5.39999V6.26538H12.9231C13.2299 6.26538 13.4861 6.36174 13.6917 6.55444C13.8972 6.74715 14 6.98733 14 7.27499V13.3808C14 13.6684 13.8972 13.9086 13.6917 14.1013C13.4861 14.294 13.2299 14.3904 12.9231 14.3904H3.07692C2.77008 14.3904 2.51389 14.294 2.30833 14.1013C2.10278 13.9086 2 13.6684 2 13.3808V7.27499C2 6.98733 2.10278 6.74715 2.30833 6.55444C2.51389 6.36174 2.77008 6.26538 3.07692 6.26538H6ZM6.66667 6.26538H9.33333V5.39999C9.33333 5.30383 9.2906 5.21569 9.20513 5.13557C9.11966 5.05544 9.02564 5.01538 8.92308 5.01538H7.07692C6.97436 5.01538 6.88034 5.05544 6.79487 5.13557C6.7094 5.21569 6.66667 5.30383 6.66667 5.39999V6.26538ZM13.3333 10.9529H9.33333V11.3856C9.33333 11.5306 9.28227 11.651 9.18013 11.7468C9.078 11.8425 8.94958 11.8904 8.79488 11.8904H7.20512C7.05042 11.8904 6.922 11.8425 6.81987 11.7468C6.71773 11.651 6.66667 11.5306 6.66667 11.3856V10.9529H2.66667V13.3808C2.66667 13.4769 2.7094 13.5651 2.79487 13.6452C2.88034 13.7253 2.97436 13.7654 3.07692 13.7654H12.9231C13.0256 13.7654 13.1197 13.7253 13.2051 13.6452C13.2906 13.5651 13.3333 13.4769 13.3333 13.3808V10.9529ZM7.33333 11.2654H8.66667V10.0154H7.33333V11.2654ZM2.66667 10.3279H6.66667V9.89518C6.66667 9.75015 6.71773 9.62976 6.81987 9.53401C6.922 9.43826 7.05042 9.39038 7.20512 9.39038H8.79488C8.94958 9.39038 9.078 9.43826 9.18013 9.53401C9.28227 9.62976 9.33333 9.75015 9.33333 9.89518V10.3279H13.3333V7.27499C13.3333 7.17884 13.2906 7.0907 13.2051 7.01057C13.1197 6.93044 13.0256 6.89038 12.9231 6.89038H3.07692C2.97436 6.89038 2.88034 6.93044 2.79487 7.01057C2.7094 7.0907 2.66667 7.17884 2.66667 7.27499V10.3279Z" />
                        </svg>
                      ) : null}
                    </div>

                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        {option.label}
                      </h3>
                      {option.description && (
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {option.description}
                        </p>
                      )}
                    </div>

                    <div className="flex-shrink-0">
                      <div
                        className={cn(
                          'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                          value === option.value
                            ? 'border-[#5865f2] bg-[#5865f2]'
                            : 'border-gray-300 bg-white',
                        )}
                      >
                        {value === option.value && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {hasError && (
              <p className="text-sm text-red-600">{errors[input.name]}</p>
            )}
          </div>
        )

      case 'file':
        return (
          <div key={input.name} className="space-y-2">
            <Label className="text-black font-circular text-sm font-medium leading-4">
              {input.label} <span>{input.required && '*'}</span>
            </Label>
            <input
              id={`${input.name}-file`}
              type="file"
              accept={input.accept}
              disabled={isLoading}
              onChange={e =>
                handleInputChange(input.name, e.target.files?.[0] || null)
              }
              className="hidden"
            />

            {value && typeof value === 'object' && 'name' in (value as File) ? (
              <button
                type="button"
                onClick={() =>
                  (
                    document.getElementById(
                      `${input.name}-file`,
                    ) as HTMLInputElement
                  )?.click()
                }
                disabled={isLoading}
                className="w-full rounded-md border border-gray-200 bg-gray-50 h-11 px-4 text-left hover:bg-gray-100 transition-colors"
              >
                <span className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {t('accounts:logoName', 'Logo: {{name}}', {
                      name: (value as File).name,
                    })}
                  </span>
                  <button
                    aria-label={t('accounts:removeFile', 'Remove file')}
                    className="ml-3 text-gray-400 hover:text-gray-600"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleInputChange(input.name, null)
                    }}
                  >
                    ✕
                  </button>
                </span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() =>
                  (
                    document.getElementById(
                      `${input.name}-file`,
                    ) as HTMLInputElement
                  )?.click()
                }
                disabled={isLoading}
                className="w-full rounded-md border border-gray-200 bg-gray-50 h-11 px-4 text-left text-[#6B7280] hover:bg-gray-100 transition-colors"
              >
                <span className="flex items-center gap-3 text-base">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="w-5 h-5 text-gray-500"
                  >
                    <path d="M21.44 11.05 12.7 19.8a6 6 0 1 1-8.49-8.49l8.49-8.49a4 4 0 1 1 5.66 5.66l-8.49 8.49a2 2 0 1 1-2.83-2.83l7.78-7.78" />
                  </svg>
                  <span className="font-medium">
                    {t('accounts:chooseFile', 'Choose file')}
                  </span>
                </span>
              </button>
            )}

            {hasError && (
              <p className="text-sm text-red-600">{errors[input.name]}</p>
            )}
          </div>
        )

      case 'checkbox':
        return (
          <div key={input.name} className="space-y-2">
            <div className="flex items-start space-x-3">
              <Checkbox
                id={input.name}
                checked={!!value}
                onCheckedChange={checked =>
                  handleInputChange(input.name, checked)
                }
                disabled={isLoading}
                className="mt-1"
              />
              <label
                htmlFor={input.name}
                className="text-base font-medium cursor-pointer"
              >
                {input.label} <span>{input.required && '*'}</span>
              </label>
            </div>
            {hasError && (
              <p className="text-sm text-red-600">{errors[input.name]}</p>
            )}
          </div>
        )

      case 'verification_pair':
        return (
          <div key={input.name} className="space-y-2">
            {input.label && (
              <Label className="text-black font-circular text-sm font-medium leading-4">
                {input.label} <span>{input.required && '*'}</span>
              </Label>
            )}
            <div className="flex gap-3">
              {input.fields?.map(field => {
                const fieldValue = formData[field.name] || ''
                const fieldError = !!errors[field.name]

                if (field.type === 'select') {
                  return (
                    <div key={field.name} className="flex-1">
                      <Select
                        value={fieldValue as string}
                        onValueChange={newValue =>
                          handleInputChange(field.name, newValue)
                        }
                        disabled={isLoading}
                      >
                        <SelectTrigger
                          className={cn(
                            'w-full h-11 border border-gray-200 rounded-sm bg-gray-50 font-circular text-base font-normal leading-5 tracking-normal',
                            fieldError && 'border-red-500 focus:border-red-500',
                          )}
                        >
                          <SelectValue placeholder={field.placeholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options?.map(option => {
                            let isDisabled = false

                            if (
                              input.name === 'verification_1' &&
                              option.value === formData.verification_source_2
                            ) {
                              isDisabled = true
                            }

                            if (
                              input.name === 'verification_2' &&
                              option.value === formData.verification_source_1
                            ) {
                              isDisabled = true
                            }

                            return (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                disabled={isDisabled}
                              >
                                {option.label}
                              </SelectItem>
                            )
                          })}
                        </SelectContent>
                      </Select>
                      {fieldError && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors[field.name]}
                        </p>
                      )}
                    </div>
                  )
                }

                if (field.type === 'text') {
                  return (
                    <div key={field.name} className="flex-1">
                      <Input
                        type="text"
                        placeholder={field.placeholder}
                        value={fieldValue as string}
                        onChange={e =>
                          handleInputChange(field.name, e.target.value)
                        }
                        className={cn(
                          'w-full h-11 border border-gray-200 rounded-sm bg-gray-50 font-circular text-base font-normal leading-5 tracking-normal px-3',
                          fieldError && 'border-red-500 focus:border-red-500',
                        )}
                        disabled={isLoading}
                      />
                      {fieldError && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors[field.name]}
                        </p>
                      )}
                    </div>
                  )
                }

                return null
              })}
            </div>
            {hasError && (
              <p className="text-sm text-red-600">{errors[input.name]}</p>
            )}
          </div>
        )

      default:
        return null
    }
  }

  const getButtonText = () => {
    if (stepIndex === 1) {
      const currentUserType = formData.userType as string
      if (currentUserType === 'artist') {
        return t('accounts:continueAsAnArtist', 'Continue as an artist')
      } else if (currentUserType === 'business') {
        return t(
          'accounts:continueAsAMusicBusiness',
          'Continue as a music business',
        )
      }
      return t('accounts:continue', 'Continue')
    }

    if (isLastStep) {
      return t('accounts:createAccount', 'Create account')
    }

    return step.submit?.text || 'Next'
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      <div className="space-y-4">{step.inputs.map(renderInput)}</div>

      <div className="space-y-4">
        <Button
          type="submit"
          variant="black"
          className="w-full"
          loading={isLoading}
          disabled={submitIsDisabled}
        >
          {isLoading
            ? step.submit?.processingText || 'Processing...'
            : getButtonText()}
        </Button>

        {onBack && (
          <div className="text-center">
            <button
              type="button"
              onClick={onBack}
              disabled={isLoading}
              className="text-[#5865f2] text-base font-medium hover:text-[#4c52d4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {/* i18next-instrument-ignore */}
              <span>← </span>
              {t('accounts:back', 'Back')}
            </button>
          </div>
        )}

        {/* TODO: SUB-870 Link to accounts.disco.ac/login */}
        {/* {stepIndex === 0 && (
          <div className="text-center text-sm text-gray-600">
            Already have an account?{' '}
            <button
              type="button"
              className="text-[#5865f2] hover:text-[#4c52d4] transition-colors font-medium underline"
            >
              Log in
            </button>
          </div>
        )} */}
      </div>
    </form>
  )
}
